/**
 * AdditionalFieldModal Component
 *
 * Enhanced modal for adding/editing additional fields with support for
 * different value types (string, number, array, JSON).
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  AdditionalField,
  ValueType,
  parseArrayValue,
  formatArrayValue,
  validateValue,
  parseInputValue,
} from '../../utils/additionalFieldsUtils';
import { useI18n } from '../../contexts/I18nContext';
import './CustomFieldModal.css';

interface AdditionalFieldModalProps {
  isOpen: boolean;
  field: AdditionalField | null;  // null = add new, object = edit existing
  onClose: () => void;
  onSave: (field: AdditionalField) => void;
  existingNames: string[];  // For validation (no duplicate names)
}

export const AdditionalFieldModal: React.FC<AdditionalFieldModalProps> = ({
  isOpen,
  field,
  onClose,
  onSave,
  existingNames
}) => {
  const { t } = useI18n();
  const [name, setName] = useState('');
  const [valueType, setValueType] = useState<ValueType>('string');
  const [rawValue, setRawValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Initialize form when field changes (edit mode)
  useEffect(() => {
    if (field) {
      setName(field.name);
      setValueType(field.valueType);
      // Format value for editing
      if (field.valueType === 'array' && Array.isArray(field.value)) {
        setRawValue(formatArrayValue(field.value));
      } else if (field.valueType === 'json' && typeof field.value === 'object') {
        setRawValue(JSON.stringify(field.value, null, 2));
      } else {
        setRawValue(String(field.value ?? ''));
      }
    } else {
      setName('');
      setValueType('string');
      setRawValue('');
    }
    setError(null);
  }, [field, isOpen]);

  // Parse preview for array type
  const arrayPreview = useMemo(() => {
    if (valueType !== 'array') return null;
    const parsed = parseArrayValue(rawValue);
    return JSON.stringify(parsed);
  }, [valueType, rawValue]);

  // Validate JSON as user types
  const jsonValidation = useMemo(() => {
    if (valueType !== 'json' || rawValue.trim() === '') return null;
    return validateValue(rawValue, 'json');
  }, [valueType, rawValue]);

  const handleSave = () => {
    // Validate name
    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    // Check for duplicate names (excluding current field in edit mode)
    const isEditing = field !== null;
    const namesToCheck = isEditing
      ? existingNames.filter(n => n !== field.name)
      : existingNames;

    if (namesToCheck.includes(name.trim())) {
      setError('A field with this name already exists');
      return;
    }

    // Validate name format (alphanumeric + underscore)
    if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(name.trim())) {
      setError('Name must start with letter and contain only letters, numbers, underscores');
      return;
    }

    // Validate value based on type
    const validation = validateValue(rawValue, valueType);
    if (!validation.valid) {
      setError(validation.error || 'Invalid value');
      return;
    }

    // Parse value
    const parsedValue = parseInputValue(rawValue, valueType);

    onSave({
      name: name.trim(),
      value: parsedValue,
      valueType,
      source: field?.source || 'user',
      rawValue,
    });
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && valueType !== 'json' && valueType !== 'array') {
      e.preventDefault();
      handleSave();
    }
    if (e.key === 'Escape') {
      onClose();
    }
  };

  const handleValueTypeChange = (newType: ValueType) => {
    setValueType(newType);
    setError(null);

    // Try to convert current value to new type
    if (newType === 'array' && rawValue && !rawValue.includes(',')) {
      // If switching to array with a single value, keep it
    } else if (newType === 'json' && rawValue) {
      // If switching to JSON, try to wrap in object if not already
      if (!rawValue.startsWith('{') && !rawValue.startsWith('[')) {
        setRawValue('{}');
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay custom-field-overlay" onClick={onClose}>
      <div
        className="custom-field-modal additional-field-modal"
        onClick={e => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        <div className="modal-header">
          <h3>{field ? 'Edit Field' : 'Add Field'}</h3>
          <button className="close-button" onClick={onClose} type="button" aria-label={t('common.close')}>×</button>
        </div>

        {error && <div className="error-banner">{error}</div>}

        <div className="modal-content">
          {/* Name Input */}
          <div className="form-group">
            <label htmlFor="field-name">Name *</label>
            <input
              id="field-name"
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g., custom_value1"
              disabled={field !== null}  // Cannot change name when editing
              autoFocus={field === null}
            />
            <span className="field-hint">Config key (letters, numbers, underscores)</span>
          </div>

          {/* Value Type Selector */}
          <div className="form-group">
            <label>{t('customFields.valueType')}</label>
            <div className="value-type-selector">
              {(['string', 'number', 'array', 'json'] as ValueType[]).map(type => (
                <button
                  key={type}
                  type="button"
                  className={`type-btn ${valueType === type ? 'active' : ''}`}
                  onClick={() => handleValueTypeChange(type)}
                >
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Value Input - varies by type */}
          <div className="form-group">
            <label htmlFor="field-value">
              {t('common.value')}
              {valueType === 'array' && <span className="type-hint"> (comma-separated)</span>}
              {valueType === 'json' && <span className="type-hint"> (JSON object)</span>}
            </label>

            {valueType === 'string' && (
              <input
                id="field-value"
                type="text"
                value={rawValue}
                onChange={e => setRawValue(e.target.value)}
                placeholder="e.g., value123"
                autoFocus={field !== null}
              />
            )}

            {valueType === 'number' && (
              <input
                id="field-value"
                type="number"
                value={rawValue}
                onChange={e => setRawValue(e.target.value)}
                placeholder="e.g., 42"
                autoFocus={field !== null}
              />
            )}

            {valueType === 'array' && (
              <>
                <textarea
                  id="field-value"
                  value={rawValue}
                  onChange={e => setRawValue(e.target.value)}
                  placeholder="e.g., item1, item2, item3"
                  rows={3}
                  autoFocus={field !== null}
                />
                {arrayPreview && (
                  <div className="value-preview">
                    <span className="preview-label">Preview:</span>
                    <code>{arrayPreview}</code>
                  </div>
                )}
              </>
            )}

            {valueType === 'json' && (
              <>
                <textarea
                  id="field-value"
                  value={rawValue}
                  onChange={e => setRawValue(e.target.value)}
                  placeholder='e.g., {"key": "value"}'
                  rows={6}
                  className={`json-input ${jsonValidation && !jsonValidation.valid ? 'invalid' : ''}`}
                  autoFocus={field !== null}
                />
                {jsonValidation && !jsonValidation.valid && (
                  <div className="validation-error">{jsonValidation.error}</div>
                )}
              </>
            )}
          </div>
        </div>

        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={onClose} type="button">
            Cancel
          </button>
          <button className="btn btn-success" onClick={handleSave} type="button">
            {field ? 'Save Changes' : 'Add Field'}
          </button>
        </div>
      </div>
    </div>
  );
};
